import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { departmentsApi, type Department } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import ProjectSelect from '@/components/ProjectSelect';
import Drawer from '@/components/Drawer';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { nextRefCode } from '@/utils/nextRefCode';

interface Editing {
  id?: string;
  organization_id: string;
  parent_id: string | null;
  code: string;
  name: string;
  default_direction_id: string | null;
}

export default function DepartmentsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр для кнопки "+ Новий підрозділ" по payroll.manage.
  // Сам dropdown залишаємо повним (user може ПЕРЕГЛЯДАТИ підрозділи orgs
  // без права створювати у них) — фільтруємо лише creation-action.
  const { permissions_by_org, hasPermissionForCurrentOrg } = (() => {
    const result = usePermissionsByOrg('payroll.manage');
    const ids = new Set(result.availableOrgs.map((o) => o.id));
    return {
      permissions_by_org: result,
      hasPermissionForCurrentOrg: (id: string) => ids.has(id),
    };
  })();
  // За замовч. orgId='' → показуємо підрозділи ВСIХ юросіб (запит власника).
  // Фільтр по юрособі лишається опційним (dropdown з «Усі»).
  const [orgId, setOrgId] = useState<string>('');
  const list = useQuery({
    queryKey: ['departments', orgId],
    queryFn: () => departmentsApi.list({ organization_id: orgId || undefined, limit: 500 }),
  });
  // Резолв organization_id → назва (для колонки «Організація» у зведеному списку).
  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])),
    [orgList.data],
  );

  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => editing!.id
      ? departmentsApi.update(editing!.id, {
          parent_id: editing!.parent_id, code: editing!.code, name: editing!.name,
          default_direction_id: editing!.default_direction_id,
        })
      : departmentsApi.create({
          organization_id: editing!.organization_id,
          parent_id: editing!.parent_id,
          code: editing!.code,
          name: editing!.name,
          default_direction_id: editing!.default_direction_id,
        }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      setEditing(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => departmentsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
  });

  const openEdit = (d: Department) => setEditing({
    id: d.id, organization_id: d.organization_id,
    parent_id: d.parent_id, code: d.code, name: d.name,
    default_direction_id: d.default_direction_id ?? null,
  });

  const cols = useMemo<ColumnDef<Department>[]>(() => [
    {
      key: 'code', header: 'Код', width: 120, importBadge: true,
      sortValue: (d) => d.code ?? '', cell: (d) => d.code,
    },
    {
      key: 'name', header: 'Назва',
      sortValue: (d) => (d.name ?? '').toLowerCase(), cell: (d) => d.name,
    },
    {
      // Зведений список усіх юросіб → показуємо приналежність.
      key: 'org', header: 'Організація', width: 220,
      sortValue: (d) => (orgName.get(d.organization_id) ?? '').toLowerCase(),
      cell: (d) => orgName.get(d.organization_id) ?? '—',
    },
    // Колонку «Батьківський» прибрано (запит власника).
    // Iконки ✎/× прибрано — редагування через double-click по рядку.
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Підрозділи</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                style={{ width: 140, flex: '0 0 auto' }}>
          <option value="">Усі юрособи</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
          ))}
        </select>
        <button className="primary" style={{ marginLeft: 'auto' }}
                disabled={!orgId || !hasPermissionForCurrentOrg(orgId)}
                title={
                  !orgId
                    ? 'Спершу оберіть юрособу'
                    : !hasPermissionForCurrentOrg(orgId)
                      ? `Немає прав створювати підрозділи у цій юрособі (потрібен payroll.manage). У вас є право у ${permissions_by_org.availableOrgs.length} з ${permissions_by_org.totalAccessibleOrgs} ваших організацій`
                      : undefined
                }
                onClick={() => setEditing({
                  organization_id: orgId, parent_id: null,
                  // Автонумерація коду (як у 1С) — редагується вручну.
                  code: nextRefCode((list.data?.items ?? []).map((d) => d.code)),
                  name: '', default_direction_id: null,
                })}>
          + Новий
        </button>
      </div>

      {editing && (
        <Drawer
          open={!!editing}
          title={editing.id ? `Підрозділ: ${editing.name || '—'}` : 'Новий підрозділ'}
          onClose={() => { setEditing(null); setError(null); }}
          footer={
            <>
              {editing.id && (
                <button className="danger"
                        onClick={() => confirm('Видалити підрозділ?').then((ok) => {
                          if (ok && editing.id) { remove.mutate(editing.id); setEditing(null); }
                        })}>
                  Видалити
                </button>
              )}
              <button onClick={() => { setEditing(null); setError(null); }}>Скасувати</button>
              <button className="primary" disabled={save.isPending}
                      onClick={() => save.mutate()}>
                Зберегти
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label>Код
              <input placeholder="Код" value={editing.code}
                     onChange={(e) => setEditing({ ...editing, code: e.target.value })} />
            </label>
            <label>Назва
              <input placeholder="Назва підрозділу" value={editing.name}
                     onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </label>
            {/* Поле «Батьківський підрозділ» прибрано з форми (запит власника
                2026-06-15). parent_id лишається у моделі (nullable), просто не
                вводиться вручну — нові підрозділи створюються без батьківського. */}
            <label>Напрямок (за замовчуванням)
              <ProjectSelect
                value={editing.default_direction_id}
                onChange={(v) => setEditing({ ...editing, default_direction_id: v })}
                organizationId={editing.organization_id}
                kind="DIRECTION"
              />
            </label>
            {error && <div className="error">{error}</div>}
          </div>
        </Drawer>
      )}

      <DataTable<Department>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(d) => d.id}
        loading={list.isLoading}
        emptyText="Немає підрозділів."
        onRowDoubleClick={openEdit}
        rowMuted={(d) => d.deletion_mark ? 0.4 : undefined}
        initialSortKey="name"
      />
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
